import dayjs from "dayjs";
import axiosInstance from "../core/index-core";
import { formatClinicianName } from "../utils/formatClinicianName";

// ---------------------------------------------------------------------------
// MOCK DATA — COMMENTED OUT (RCA UX5)
// These mock constants are dead code — not referenced by any real API function.
// Kept as comments for reference during development/testing only.
// ---------------------------------------------------------------------------
// Mock data for appointments
// const mockAppointments = [
//   {
//     id: "1",
//     patientName: "Joe Black",
//     patientAge: 38,
//     appointmentType: "Follow up",
//     time: "08:00 am",
//     status: "completed",
//   },
//   {
//     id: "2",
//     patientName: "Olivia Martin",
//     patientAge: 48,
//     appointmentType: "Consultation",
//     time: "09:00 am",
//     status: "completed",
//   },
//   {
//     id: "3",
//     patientName: "Sophia Reed",
//     patientAge: 27,
//     appointmentType: "Follow up",
//     time: "10:00 am",
//     status: "active",
//   },
//   {
//     id: "4",
//     patientName: "Ava Parker",
//     patientAge: 30,
//     appointmentType: "Follow up",
//     time: "06:00 pm",
//     status: "in-progress",
//   },
//   {
//     id: "5",
//     patientName: "Ethan Brooks",
//     patientAge: 37,
//     appointmentType: "Consultation",
//     time: "07:00 pm",
//     status: "scheduled",
//   },
//   // {
//   //   id: "6",
//   //   patientName: "Benjamin Harris",
//   //   patientAge: 55,
//   //   appointmentType: "Follow up",
//   //   time: "08:00 pm",
//   //   status: "scheduled",
//   // },
// ];

// Mock data for sessions
// const mockSessions = [
//   {
//     id: "1",
//     title: "Session #1",
//     date: "Dec 02, 2024",
//     time: "5:30 pm",
//     patientId: "1",
//     category: "Therapists",
//   },
//   {
//     id: "2",
//     title: "Session #2",
//     date: "Dec 16, 2024",
//     time: "5:45 pm",
//     patientId: "2",
//     category: "Therapists",
//   },
//   {
//     id: "3",
//     title: "Session #3",
//     date: "Dec 23, 2024",
//     time: "5:45 pm",
//     patientId: "3",
//     category: "Prescribers",
//   },
//   {
//     id: "4",
//     title: "Session #3",
//     date: "Jan 07, 2025",
//     time: "6:00 pm",
//     patientId: "4",
//     category: "Prescribers",
//     isActive: true,
//   },
// ];

// Mock patient details
// const mockPatientDetails = {
//   4: {
//     name: "Ava Parker",
//     age: 30,
//     gender: "Female",
//     contactNumber: "(555) 123-4567",
//     sessionType: "Prescription",
//   },
// };

// Mock transcript data
// const mockTranscript = {
//   4: [
//     {
//       speaker: "Dr. Brown",
//       timestamp: "00:02",
//       text: "Good evening, Mrs. Parker. How have you been feeling since our last session?",
//     },
//     {
//       speaker: "Ms. Parker",
//       timestamp: "00:07",
//       text: "Evening, Dr. Brown. I've been feeling slightly better, but my anxiety spikes in the evenings.",
//     },
//     {
//       speaker: "Dr. Brown",
//       timestamp: "00:12",
//       text: "I see. Have you been practicing the breathing exercises we discussed?",
//     },
//     {
//       speaker: "Ms. Parker",
//       timestamp: "00:18",
//       text: "Yes, but it's been hard to stay consistent.",
//     },
//     {
//       speaker: "Dr. Brown",
//       timestamp: "00:24",
//       text: "That's understandable. Let's adjust your routine and explore other coping strategies. Have you noticed any changes in your sleep patterns?",
//     },
//     {
//       speaker: "Ms. Parker",
//       timestamp: "00:32",
//       text: "I still struggle with falling asleep, but I sleep through the night once I do.",
//     },
//     {
//       speaker: "Dr. Brown",
//       timestamp: "00:40",
//       text: "That's progress. We'll review your current plan and make adjustments. Let's also check how you're responding to the medication.",
//     },
//   ],
// };

// Mock AI notes
// const mockAINotes = {
//   4: {
//     chiefComplaint: "Depression",
//     history: "",
//     problemDesc:
//       "xxx is a 40-yo female who presents for telehealth follow-up appointment",
//     currentMedication: [
//       "Current medication: Prozac 40mg oral",
//       "Had recent illness...",
//     ],
//     importantInfo: "Important Life Event and Information",
//     traumaHistory: "Trauma History: Difficult separation/divorce.",
//     progress: "Progress since last visit: Slightly improved",
//     mood: "Mood:",
//   },
// };

// Mock past summary
// const mockPastSummary = {
//   4: {
//     date: "January 15, 2024",
//     primaryConcern:
//       "Generalized Anxiety Disorder (GAD) with mild depressive symptoms.",
//     medicationHistory:
//       "Currently on Sertraline 50mg/day, prescribed on Dec 10, 2023.",
//     therapyProgress:
//       "Patient reports a 30% improvement in managing daily stressors.",
//     recentChallenges:
//       "Difficulty sleeping and occasional panic episodes (2-3 times/week).",
//     lifestyleChanges: "Started guided meditation and light exercise routine.",
//     followUpNotes:
//       "Advised to track mood daily; patient expressed concerns about work-related stress",
//     riskAssessment:
//       "No suicidal ideation, but reports occasional feelings of hopelessness.",
//     nextSteps:
//       "Evaluate medication effectiveness and introduce CBT techniques for stress management.",
//     upcomingGoals:
//       "Improve sleep quality and build coping mechanisms for workplace anxiety.",
//   },
// };

export const getAppointments = async ({ clinicianId, startDate }) => {
  if (!clinicianId) throw new Error("clinicianId is required");
  if (!startDate) throw new Error("startDate is required");
  const ddmmyyyy = dayjs(startDate).format("MM/DD/YYYY");
  return axiosInstance.get(`clinician/${clinicianId}/appointments`, {
    params: { start_date: ddmmyyyy },
  });
};

export const getSessions = async (patient_id = 6019466, params) => {
  return axiosInstance.get(`patient/${patient_id}/sessions`, {
    params
  });
};

export const getPatientDetails = async (id, sessionId) => {
  return axiosInstance.get(`patient/${id}`);
};

export const getTranscript = async (patientId, sessionId) => {
  return axiosInstance.get(`patient/${patientId}/sessions/${sessionId}/transcription`)
};

export const getRecordingTranscript = async (patientId, sessionId) => {
  return axiosInstance.get(`patient/${patientId}/sessions/${sessionId}/recording-transcript`)
};

// Demo/testing only: upload a plain-text transcript for a session so reviewers can
// exercise note generation without recording a live conversation. The backend
// stores it as the session transcription; Generate AI Notes then runs off it.
export const putTranscript = async (patientId, sessionId, content) => {
  return axiosInstance.put(
    `patient/${patientId}/sessions/${sessionId}/transcription`,
    { content, mime_type: "text" }
  );
};


export const getPastSummary = async ({ patientId, sessionIds, excludeSessionId }) => {
  if (!patientId) throw new Error("patientId is required");
  if (
    sessionIds == null ||
    (Array.isArray(sessionIds) && sessionIds.length === 0)
  ) {
    throw new Error("sessionIds (number or non-empty array) is required");
  }
  const toISODate = (d) => dayjs(d).format("YYYY-MM-DD");

  const payload = {
    session_ids: Array.isArray(sessionIds) ? sessionIds : [sessionIds],
    ...(excludeSessionId && { exclude_session_id: excludeSessionId }),
  };

  return axiosInstance.post(`patient/${patientId}/past-summary`, payload);
};

// ---------------------------------------------------------------------------
// MOCK FUNCTIONS — COMMENTED OUT (RCA UX5)
// generateAINotes: Was returning hardcoded mock data instead of calling API.
//   Not imported anywhere — actual AI note generation uses axiosInstance.post
//   to /generate-ai-notes endpoint directly in ManualNotes.js.
// submitFeedback: Was returning a mock promise. Called by old FeedbackModal
//   in Session.js — real feedback is now handled in AINotesView.js via
//   axiosInstance.post to /notes/ai-feedback endpoint.
// ---------------------------------------------------------------------------
// export const generateAINotes = async (transcript) => {
//   return new Promise((resolve) => {
//     setTimeout(() => {
//       resolve(mockAINotes["4"]);
//     }, 1500);
//   });
// };

// export const submitFeedback = async (feedback) => {
//   return new Promise((resolve) => {
//     setTimeout(() => {
//       resolve();
//     }, 500);
//   });
// };

/**
 * GET /api/admin/feedback — list AI-note feedback for the admin Feedback view.
 *
 * The axios baseURL is `.../v1/` (no `/api`), so this admin endpoint is called
 * with a leading `/api` exactly like PracticeService hits `/api/admin/practices`.
 * Returns { feedback: [...], page, page_size }.
 */
export const listFeedback = async ({ pageSize = 200 } = {}) => {
  const { data } = await axiosInstance.get(`/api/admin/feedback`, { params: { page_size: pageSize } });
  return data; // { feedback: [...], page, page_size }
};

export const getClinician = async (id) => {
  const { data } = await axiosInstance.get(`clinician/${id}`);
  return data;
};

export const getClinicianByToken = async (id) => {
  const { data } = await axiosInstance.get(`clinician/details`);
  return data;
};

/**
 * PATCH /clinician/me — update the authenticated clinician's own name fields.
 * Caller is identified by their auth token; they can only edit themselves.
 * @param {{ first_name?: string, middle_name?: string, last_name?: string }} payload
 */
export const updateMyProfile = async (payload) => {
  const { data } = await axiosInstance.patch("clinician/me", payload);
  return data;
};

/**
 * POST /clinician/me/avatar — upload a profile picture for the authenticated clinician.
 * Accepts jpeg, png, or webp; max 5 MB. Backend field name is `file`.
 * @param {File} file
 * @returns {object} updated clinician profile including avatar_url
 */
export const uploadMyAvatar = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await axiosInstance.post("clinician/me/avatar", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

/**
 * DELETE /clinician/me/avatar — clear the authenticated clinician's avatar.
 * @returns {object} updated clinician profile with avatar_url set to null
 */
export const deleteMyAvatar = async () => {
  const { data } = await axiosInstance.delete("clinician/me/avatar");
  return data;
};

export const getCliniciansByIds = async (ids) => {
  const results = await Promise.all(
    ids.map(async (id) => {
      try {
        const c = await getClinician(id);
        return {
          value: id,
          label: formatClinicianName(c) || `Clinician ${id}`,
          avatar: c?.profile_picture_url || null,
          raw: c,
        };
      } catch (e) {
        return { value: id, label: `Clinician ${id}`, avatar: null, raw: null };
      }
    })
  );
  return results;
};

export const getNoteCommentAPI = async (patientId, sessionId) => {
  if (!patientId || !sessionId) return { note: "" };
  const { data } = await axiosInstance.get(
    `patient/${patientId}/sessions/${sessionId}/note_comment`
  );
  return data;
};

export const saveNoteCommentAPI = async (patientId, sessionId, note) => {
  if (!patientId || !sessionId) return null;
  const payload = { data: note ?? "" };
  const { data } = await axiosInstance.put(
    `patient/${patientId}/sessions/${sessionId}/note_comment`,
    payload
  );
  return data;
};
